import { Link } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTAButtons } from "@/components/site/CTAButtons";
import { SITE } from "@/config/site";
import faceBgAssetUrl from "@/assets/izhub-face-bg.webp";
const faceBgAsset = { url: faceBgAssetUrl };
import { ServiceWorkGallery } from "@/components/site/ServiceWorkGallery";

export interface ServiceDetailData {
  title: string;
  heroTitle: React.ReactNode;
  intro: string;
  image: string;
  imageAlt: string;
  forWho: string;
  paragraphs: string[];
  includes: string[];
  quoteService: "regular" | "one-off" | "deep" | "tenancy" | "military" | "commercial" | "airbnb";
}

export function ServiceDetail({ data }: { data: ServiceDetailData }) {
  return (
    <>
      <PageHero title={data.heroTitle} intro={data.intro} withBackground backgroundImage={faceBgAsset.url}>
        <CTAButtons />
      </PageHero>

      <section className="border-y border-border bg-background/85 backdrop-blur-sm">
        <div className="container-x py-16 md:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <img
              src={data.image}
              alt={data.imageAlt}
              loading="lazy"
              className="rounded-3xl w-full h-auto object-cover aspect-[4/3] shadow-soft"
            />
          </div>
          <div>
            <h2 className="font-display text-3xl md:text-4xl">What's included</h2>
            <p className="mt-3 text-sm text-primary">
              <strong className="font-medium">Best for:</strong> {data.forWho}
            </p>
            <ul className="mt-6 space-y-2.5">
              {data.includes.map((item) => (
                <li key={item} className="flex gap-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 rainbow-text" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote" search={{ service: data.quoteService }} className="btn-primary">
                Get my instant quote
              </Link>
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-outline">
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <ServiceWorkGallery service={data.quoteService} />

      <section className="bg-background/85 backdrop-blur-sm">
        <div className="container-x py-16 md:py-24 max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl">About this service</h2>
          <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
            {data.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 bg-background/85">
        <div className="container-x">
          <div className="rounded-3xl bg-sage-soft/60 border border-sage/30 p-8 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl max-w-2xl mx-auto">
              Ready to book your {data.title.toLowerCase()}?
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Get an instant price online, or message us on WhatsApp — we reply fast.
            </p>
            <div className="mt-8 flex justify-center">
              <CTAButtons />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
